import { useState, useRef, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { clsx } from 'clsx'
import { CATEGORY_GROUPS, getCategoriesByGroup } from '@/utils/categories'
import { CategoryGroupIcon } from '@/components/icons/CategoryGroupIcons'

export function MegaMenu() {
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close on Escape
  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen) setIsOpen(false)
  }, [isOpen])

  useEffect(() => {
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [handleEscape])

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    setIsOpen(true)
  }

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 150)
  }

  return (
    <div
      ref={menuRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && isOpen) {
            setIsOpen(false)
          } else if (e.key === 'ArrowDown' && !isOpen) {
            e.preventDefault()
            setIsOpen(true)
          }
        }}
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={clsx(
          'btn-ghost text-sm flex items-center gap-1',
          isOpen && 'bg-slate-100'
        )}
      >
        Categories
        <ChevronDown className={clsx('w-4 h-4 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {/* Mega Menu Dropdown */}
      {isOpen && (
        <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-[800px] max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-xl border border-slate-200 animate-fade-in z-50">
          {/* Arrow */}
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-l border-t border-slate-200 rotate-45" />

          <div className="p-6 relative">
            {/* Header */}
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900">Browse Categories</h3>
              <Link
                to="/categories"
                onClick={() => setIsOpen(false)}
                className="text-sm text-primary-600 hover:underline"
              >
                View All Categories
              </Link>
            </div>

            {/* Category Grid */}
            <div className="grid grid-cols-3 gap-6">
              {CATEGORY_GROUPS.map((group) => {
                const categories = getCategoriesByGroup(group.id)
                return (
                  <div key={group.id}>
                    {/* Group Header */}
                    <Link
                      to={`/browse?group=${group.id}`}
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-2 font-medium text-slate-900 hover:text-primary-600 mb-3"
                    >
                      <CategoryGroupIcon iconKey={group.icon} className="w-6 h-6" />
                      <span>{group.name}</span>
                    </Link>

                    {/* Category Links */}
                    <ul className="space-y-1.5">
                      {categories.map((category) => (
                        <li key={category.id}>
                          <Link
                            to={`/browse?category=${category.id}`}
                            onClick={() => setIsOpen(false)}
                            className="text-sm text-slate-600 hover:text-primary-600 flex items-center gap-2"
                          >
                            <span>{category.name}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              })}
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between">
              <div className="text-sm text-slate-500">
                Browse categories across NZ
              </div>
              <Link
                to="/create"
                onClick={() => setIsOpen(false)}
                className="text-sm font-medium text-primary-600 hover:underline"
              >
                Sell Something
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
